import { useCallback, useEffect, useState } from 'react'
import { useTickerApi } from './useTickerApi'
import StatsSource from './StatsSource'
import TickerRows from './TickerRows'
import { STANDINGS_PRESETS, STANDINGS_TOKENS, renderTemplate, standingsContext } from './tickerFormat'

// One ticker line per division. A full division is 7-11 teams and will not fit
// 129 characters once everyone has played, so the line is capped at the top N —
// using the abbreviations the league publishes on its own standings page.
export default function Standings() {
  const api = useTickerApi()
  const [seasonId, setSeasonId] = useState('')
  const [divisions, setDivisions] = useState([])
  const [top, setTop] = useState(5)
  const [playedOnly, setPlayedOnly] = useState(true)
  const [league, setLeague] = useState('')

  const load = useCallback(() => {
    if (!seasonId) return
    api.listStandings(seasonId, { top: playedOnly ? 20 : top })
      .then(r => setDivisions(r?.divisions || []))
      .catch(() => setDivisions([]))
    api.listSeasons().then(r => {
      const s = (r?.seasons || []).find(x => String(x.id) === String(seasonId))
      setLeague(s?.league || '')
    }).catch(() => {})
  }, [api, seasonId, top, playedOnly])

  useEffect(() => { load() }, [load])

  // Filtering to teams that have played happens here rather than in SQL: early
  // in a season most of a division sits at 0-0-0 and crowds out the teams with
  // results, but mid-season every team qualifies and the cap does the work.
  const rows = divisions.map(d => {
    const teams = (playedOnly ? d.teams.filter(t => (t.gp ?? 0) > 0) : d.teams)
    return { ...d, teams: (teams.length ? teams : d.teams).slice(0, top) }
  })

  return (
    <div>
      <StatsSource seasonId={seasonId} setSeasonId={setSeasonId} onRefreshed={load}>
        <label style={{ display: 'grid', gap: 4 }}>
          <span className="muted" style={{ fontSize: 12 }}>Teams per line</span>
          <input type="number" min="1" max="20" value={top} style={{ width: 70 }}
            onChange={e => setTop(Math.max(1, Number(e.target.value) || 5))} />
        </label>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
          <input type="checkbox" checked={playedOnly} onChange={e => setPlayedOnly(e.target.checked)} />
          Only teams that have played
        </label>
      </StatsSource>

      <TickerRows
        storageKey="tbtv.ticker.standings.settings"
        presets={STANDINGS_PRESETS}
        tokens={STANDINGS_TOKENS}
        rows={rows}
        renderItem={(d, template) => renderTemplate(template, standingsContext(d, { league }))}
        rowLabel={(d) => String(d.division || '').replace(/^Class /, '')}
        emptyMessage="No standings stored yet. Use Refresh from league above."
      />
    </div>
  )
}
